import React, { useState, useEffect } from 'react';
import { apiFetch, api } from '../lib/api';
import { Card, Btn, SLabel } from '../components/ui';
import EmailSearch from '../components/EmailSearch';
import { useConfirm } from '../components/ConfirmModal';
import useIsMobile from '../hooks/useIsMobile';
const uuidv4 = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); });

export default function ViewerAccessPage() {
  const isMobile = useIsMobile();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [artists, setArtists] = useState([]);
  const [selArtist, setSelArtist] = useState('');
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newContact, setNewContact] = useState({ name: '', email: '', groupIds: [] });

  useEffect(() => {
    api.getArtists().then(d => {
      const list = d.artists || [];
      setArtists(list);
      if (list.length) {
        setSelArtist(list[0].id);
        setGroups(list[0].viewerGroups || []);
        setContacts(list[0].viewerContacts || []);
      }
    });
  }, []);

  const artistObj = artists.find(a => a.id === selArtist);

  const switchArtist = (id) => {
    setSelArtist(id);
    const a = artists.find(x => x.id === id);
    setGroups(a?.viewerGroups || []);
    setContacts(a?.viewerContacts || []);
  };

  const save = async (newGroups, newContacts) => {
    setSaving(true);
    try {
      const res = await apiFetch(`/artists/${selArtist}`, {
        method: 'PATCH',
        body: JSON.stringify({ viewerGroups: newGroups || groups, viewerContacts: newContacts || contacts }),
      });
      setArtists(prev => prev.map(a => a.id === selArtist ? res : a));
      setGroups(res.viewerGroups || []);
      setContacts(res.viewerContacts || []);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const addGroup = async () => {
    if (!newGroupName.trim()) return;
    const g = { id: uuidv4(), name: newGroupName.trim(), always: false, emailAlways: false };
    const updated = [...groups, g];
    setGroups(updated);
    setNewGroupName('');
    await save(updated);
  };

  const toggleGroupAlways = async (gid) => {
    const updated = groups.map(g => g.id === gid ? { ...g, always: !g.always } : g);
    setGroups(updated);
    await save(updated);
  };

  const toggleGroupEmail = async (gid) => {
    const updated = groups.map(g => g.id === gid ? { ...g, emailAlways: !g.emailAlways } : g);
    setGroups(updated);
    await save(updated);
  };

  const deleteGroup = async (gid) => {
    if (!await confirmAction(`Delete this group?`, { title: 'Delete Group', confirmLabel: 'Delete' })) return;
    const updated = groups.filter(g => g.id !== gid);
    const updatedContacts = contacts.map(c => ({ ...c, groupIds: (c.groupIds || []).filter(id => id !== gid) }));
    setGroups(updated);
    setContacts(updatedContacts);
    await save(updated, updatedContacts);
  };

  const addContact = async () => {
    if (!newContact.email?.trim()) return;
    const c = { id: uuidv4(), name: newContact.name.trim(), email: newContact.email.trim().toLowerCase(), groupIds: newContact.groupIds, emailAlways: false };
    const updated = [...contacts, c];
    setContacts(updated);
    setNewContact({ name: '', email: '', groupIds: [] });
    await save(undefined, updated);
  };

  const toggleContactGroup = async (cid, gid) => {
    const updated = contacts.map(c => {
      if (c.id !== cid) return c;
      const gids = c.groupIds || [];
      return { ...c, groupIds: gids.includes(gid) ? gids.filter(id => id !== gid) : [...gids, gid] };
    });
    setContacts(updated);
    await save(undefined, updated);
  };

  const toggleContactEmail = async (cid) => {
    const updated = contacts.map(c => c.id === cid ? { ...c, emailAlways: !c.emailAlways } : c);
    setContacts(updated);
    await save(undefined, updated);
  };

  const deleteContact = async (cid) => {
    const updated = contacts.filter(c => c.id !== cid);
    setContacts(updated);
    await save(undefined, updated);
  };

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: -0.5, color: 'var(--text)' }}>Viewer Access</h1>
        <p style={{ fontSize: 14, color: 'var(--text2)', marginTop: 4 }}>Control who can see day sheets and receive emails</p>
      </div>

      {/* Artist selector */}
      {artists.length > 1 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {artists.map(a => (
            <button key={a.id} onClick={() => switchArtist(a.id)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0, minWidth: 68, opacity: selArtist === a.id ? 1 : 0.5 }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: `${a.color || '#1E40AF'}20`, border: `2.5px solid ${selArtist === a.id ? (a.color || 'var(--blue)') : 'transparent'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
                {a.photo ? <img src={a.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (a.emoji || '🎵')}
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: selArtist === a.id ? 'var(--text)' : 'var(--text3)' }}>{a.name?.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      )}

      {/* ═══ GROUPS ═══ */}
      <Card style={{ padding: 0, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--text3)', margin: 0, textTransform: 'uppercase' }}>
            <i className="fa-solid fa-folder" style={{ marginRight: 5, fontSize: 9 }}></i>Groups
          </p>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{groups.length}</span>
        </div>

        {groups.map(g => (
          <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{g.name}</p>
              <p style={{ fontSize: 11, color: 'var(--text3)', margin: '2px 0 0' }}>
                {contacts.filter(c => (c.groupIds || []).includes(g.id)).length} member{contacts.filter(c => (c.groupIds || []).includes(g.id)).length !== 1 ? 's' : ''}
              </p>
            </div>
            <button onClick={() => toggleGroupAlways(g.id)}
              style={{ padding: '5px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                background: g.always ? 'rgba(34,197,94,0.12)' : 'var(--bg)', color: g.always ? '#22c55e' : 'var(--text3)' }}>
              {g.always ? '★ Default' : 'Per-sheet'}
            </button>
            <button onClick={() => toggleGroupEmail(g.id)}
              style={{ padding: '5px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                background: g.emailAlways ? 'rgba(30,64,175,0.12)' : 'var(--bg)', color: g.emailAlways ? 'var(--blue)' : 'var(--text3)' }}>
              {g.emailAlways ? '✓ Auto-email' : 'No auto-email'}
            </button>
            <button onClick={() => deleteGroup(g.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 12, padding: 4 }}>
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, padding: '12px 20px' }}>
          <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addGroup()}
            placeholder="New group name..."
            style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }} />
          <button onClick={addGroup} disabled={!newGroupName.trim()}
            style={{ padding: '8px 14px', borderRadius: 8, background: newGroupName.trim() ? 'var(--blue)' : 'var(--bg)', border: 'none', color: newGroupName.trim() ? '#fff' : 'var(--text3)', fontSize: 12, fontWeight: 700, cursor: newGroupName.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }}>
            + Add
          </button>
        </div>
      </Card>

      {/* ═══ CONTACTS ═══ */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--text3)', margin: 0, textTransform: 'uppercase' }}>
            <i className="fa-solid fa-users" style={{ marginRight: 5, fontSize: 9 }}></i>Contacts
          </p>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{contacts.length}</span>
        </div>

        {contacts.map(c => (
          <div key={c.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{c.name || c.email}</p>
                {c.name && <p style={{ fontSize: 11, color: 'var(--text3)', margin: '1px 0 0' }}>{c.email}</p>}
              </div>
              <button onClick={() => toggleContactEmail(c.id)}
                style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                  background: c.emailAlways ? 'rgba(30,64,175,0.12)' : 'var(--bg)', color: c.emailAlways ? 'var(--blue)' : 'var(--text3)' }}>
                {c.emailAlways ? '★ Default' : 'Per-sheet'}
              </button>
              <button onClick={() => deleteContact(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 11, padding: 4, opacity: 0.6 }}>✕</button>
            </div>
            {groups.length > 0 && (
              <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                {groups.map(g => {
                  const inGroup = (c.groupIds || []).includes(g.id);
                  return (
                    <button key={g.id} onClick={() => toggleContactGroup(c.id, g.id)}
                      style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        background: inGroup ? 'rgba(30,64,175,0.1)' : 'var(--bg)',
                        border: `1px solid ${inGroup ? 'var(--blue)' : 'var(--border)'}`,
                        color: inGroup ? 'var(--blue)' : 'var(--text3)' }}>
                      {inGroup ? '✓' : '○'} {g.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Add contact */}
        <div style={{ padding: '12px 20px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input value={newContact.name} onChange={e => setNewContact(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Name" style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }} />
            <div style={{ flex: 1.5 }}>
              <EmailSearch value={newContact.email} onChange={email => setNewContact(prev => ({ ...prev, email }))} placeholder="Email..." />
            </div>
            <button onClick={addContact} disabled={!newContact.email?.includes('@')}
              style={{ padding: '8px 14px', borderRadius: 8, background: newContact.email?.includes('@') ? 'var(--blue)' : 'var(--bg)', border: 'none', color: newContact.email?.includes('@') ? '#fff' : 'var(--text3)', fontSize: 12, fontWeight: 700, cursor: newContact.email?.includes('@') ? 'pointer' : 'default', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              + Add
            </button>
          </div>
        </div>
      </Card>

      {ConfirmDialog}
    </div>
  );
}
